import React, { useEffect, useState, useCallback, useRef } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import useIsMobile from '../../../hooks/useIsMobile'
import { useAuth } from '../../../context/auth'
import ResponseDetailsModal from '../../User/Respon/ResponseDetailsModal'

const MainSaleRecordList = () => {
  const navigate = useNavigate()
  const [auth] = useAuth()
  const groupId = auth?.user?.group

  const isMobile = useIsMobile()
  const [groupedLists, setGroupedLists] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const [showResponseModal, setShowResponseModal] = useState(false)
  const [selectedResponseId, setSelectedResponseId] = useState(null)

  // Observer ref for infinite scroll
  const observerRef = useRef()
  const lastRowRef = useCallback(
    (node) => {
      if (loading || loadingMore) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1)
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [loading, loadingMore, hasMore]
  )

  // Initial fetch
  useEffect(() => {
    const fetchList = async () => {
      try {
        if (!groupId) {
          console.error('No groupId found. Please log in.')
          navigate('/login')
          return
        }

        setLoading(true)

        const { data } = await axios.get('/api/v1/main/sale-record/lists', {
          params: { groupId, page: 1, limit: 50 },
        })

        if (data.success) {
          setGroupedLists(data.data)
          setHasMore(data.pagination.hasMore)
        }
      } catch (error) {
        console.error(
          'Failed to fetch lists:',
          error.response?.data || error.message
        )
      } finally {
        setLoading(false)
      }
    }

    fetchList()
  }, [groupId, navigate])

  // Fetch more when page changes
  useEffect(() => {
    if (page === 1) return

    const fetchMore = async () => {
      try {
        setLoadingMore(true)

        const { data } = await axios.get('/api/v1/main/sale-record/lists', {
          params: { groupId, page, limit: 50 },
        })

        if (data.success) {
          // Merge new data with existing data
          setGroupedLists((prev) => {
            const merged = [...prev]

            data.data.forEach((newDay) => {
              const existingDayIndex = merged.findIndex(
                (d) => d._id === newDay._id
              )

              if (existingDayIndex >= 0) {
                // Same date exists - merge teams
                newDay.teams.forEach((newTeam) => {
                  const existingTeamIndex = merged[
                    existingDayIndex
                  ].teams.findIndex(
                    (t) => t.teamId?.toString() === newTeam.teamId?.toString()
                  )

                  if (existingTeamIndex >= 0) {
                    // Same team exists - merge records
                    merged[existingDayIndex].teams[existingTeamIndex].records =
                      [
                        ...merged[existingDayIndex].teams[existingTeamIndex]
                          .records,
                        ...newTeam.records,
                      ]
                    merged[existingDayIndex].teams[
                      existingTeamIndex
                    ].teamTotalQty += newTeam.teamTotalQty
                    merged[existingDayIndex].teams[
                      existingTeamIndex
                    ].teamTotalSales += newTeam.teamTotalSales
                  } else {
                    // New team - add to teams array
                    merged[existingDayIndex].teams.push(newTeam)
                  }
                })

                // Recalculate day totals
                merged[existingDayIndex].dayTotalQty = merged[
                  existingDayIndex
                ].teams.reduce((sum, t) => sum + t.teamTotalQty, 0)
                merged[existingDayIndex].dayTotalSales = merged[
                  existingDayIndex
                ].teams.reduce((sum, t) => sum + t.teamTotalSales, 0)
              } else {
                // New date - add to array
                merged.push(newDay)
              }
            })

            return merged
          })

          setHasMore(data.pagination.hasMore)
        }
      } catch (error) {
        console.error('Failed to fetch more:', error)
      } finally {
        setLoadingMore(false)
      }
    }

    fetchMore()
  }, [page, groupId])

  const truncateText = (text, wordLimit) => {
    if (!text) return ''
    const words = text.split(' ')
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...'
    }
    return text
  }

  const handleReturnHome = () => {
    navigate('/main/dashboard')
  }

  const openResponseModal = (recordId) => {
    setSelectedResponseId(recordId)
    setShowResponseModal(true)
  }

  const closeResponseModal = () => {
    setShowResponseModal(false)
    setSelectedResponseId(null)
  }

  return (
    <Layout title={'Rekod Jualan Buku'}>
      <div className='pt-3 mx-2'>
        {!isMobile && (
          <div className='d-flex mb-3 justify-content-between align-items-center'>
            <h5 className='mb-0' style={{ color: '#556B2F' }}>
              Team Book Sale Record
            </h5>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              onClick={handleReturnHome}
            >
              <i className='bi bi-house-fill'></i>
            </button>
          </div>
        )}

        <div className='table-responsive'>
          <table className='table'>
            <thead>
              <tr>
                <th scope='col'>Book</th>
                {!isMobile && (
                  <>
                    <th scope='col' className='text-end'>
                      Price
                    </th>
                    <th scope='col' className='text-center'>
                      Qty
                    </th>
                    <th scope='col' className='text-end'>
                      Total
                    </th>
                    <th className='text-end' style={{ whiteSpace: 'nowrap' }}>
                      Kad Respon
                    </th>
                    <th>Remark</th>
                    <th>Team</th>
                    <th></th>
                  </>
                )}
                {isMobile && <th className='text-end'>Total</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={isMobile ? '2' : '8'}
                    className='text-center py-4'
                  >
                    <div
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    Loading...
                  </td>
                </tr>
              ) : groupedLists.length > 0 ? (
                <>
                  {groupedLists.map((dayData, dayIndex) => (
                    <React.Fragment key={dayData._id}>
                      {/* Date Header */}
                      <tr>
                        <td
                          colSpan={isMobile ? '2' : '8'}
                          className='bg-light fw-bold text-center'
                          style={{ color: '#0c432a' }}
                        >
                          {new Date(dayData._id).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </td>
                      </tr>

                      {/* Teams and Records */}
                      {dayData.teams.map((team, teamIndex) => (
                        <React.Fragment key={team.teamId || 'no-team'}>
                          {/* Team Records - Desktop */}
                          {!isMobile &&
                            team.records.map((item) => (
                              <tr key={item._id}>
                                <td>{truncateText(item.description, 8)}</td>
                                <td className='text-end'>
                                  {item.price.toFixed(2)}
                                </td>
                                <td className='text-center'>{item.qty}</td>
                                <td className='text-end'>
                                  {(item.price * item.qty).toFixed(2)}
                                </td>
                                <td className='text-end'>
                                  {item.contactData ? (
                                    <button
                                      type='button'
                                      className='btn btn-link p-0 text-decoration-none'
                                      onClick={() =>
                                        openResponseModal(item.contactData)
                                      }
                                    >
                                      {item.contactData.toString().slice(-6)}
                                    </button>
                                  ) : null}
                                </td>
                                <td>{item.remark}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {team.teamName}
                                </td>
                                <td>
                                  {item.placeCat === 1
                                    ? 'PB.'
                                    : item.placeCat === 2
                                    ? 'Tamu'
                                    : 'Conf.'}
                                </td>
                              </tr>
                            ))}

                          {/* Team Records - Mobile */}
                          {isMobile &&
                            team.records.map((item) => (
                              <React.Fragment key={item._id}>
                                <tr>
                                  <td>
                                    {truncateText(item.description, 8)}
                                    <div>
                                      RM {item.price.toFixed(2)} x {item.qty}
                                    </div>
                                  </td>
                                  <td className='text-end'>
                                    RM {(item.price * item.qty).toFixed(2)}
                                    <div style={{ whiteSpace: 'nowrap' }}>
                                      {team.teamName}
                                    </div>
                                  </td>
                                </tr>
                                <tr
                                  style={{
                                    borderBottom: item.remark
                                      ? 'none'
                                      : '1px solid #635a5a',
                                  }}
                                >
                                  <td>
                                    Kad Respon:{' '}
                                    {item.contactData ? (
                                      <button
                                        type='button'
                                        className='btn btn-link p-0 text-decoration-none'
                                        onClick={() =>
                                          openResponseModal(item.contactData)
                                        }
                                      >
                                        {item.contactData.toString().slice(-6)}
                                      </button>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td className='text-end'>
                                    {item.placeCat === 1
                                      ? 'PB.'
                                      : item.placeCat === 2
                                      ? 'Tamu'
                                      : 'Conf.'}
                                  </td>
                                </tr>
                                {item.remark && (
                                  <tr>
                                    <td
                                      colSpan={2}
                                      className='py-1'
                                      style={{
                                        borderBottom: '1px solid #635a5a',
                                      }}
                                    >
                                      Catatan: {item.remark}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}

                          {/* Team Subtotal Row */}
                          {!isMobile ? (
                            <tr className='table-secondary'>
                              <td className='fw-semibold'>{team.teamName}</td>
                              <td></td>
                              <td className='text-center fw-semibold'>
                                {team.teamTotalQty}
                              </td>
                              <td className='text-end fw-semibold'>
                                {team.teamTotalSales.toFixed(2)}
                              </td>
                              <td colSpan={4}></td>
                            </tr>
                          ) : (
                            <tr className='table-secondary'>
                              <td className='fw-semibold'>{team.teamName}</td>
                              <td
                                className='text-end fw-semibold'
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                RM {team.teamTotalSales.toFixed(2)} (
                                {team.teamTotalQty})
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Day Total Row */}
                      {!isMobile ? (
                        <tr
                          className='fw-bold bg-light'
                          style={{ borderBottom: '2px solid #0c432a' }}
                          ref={
                            dayIndex === groupedLists.length - 1
                              ? lastRowRef
                              : null
                          }
                        >
                          <td style={{ color: '#0c432a' }}>DAY TOTAL</td>
                          <td></td>
                          <td
                            className='text-center'
                            style={{ color: '#0c432a' }}
                          >
                            {dayData.dayTotalQty}
                          </td>
                          <td className='text-end' style={{ color: '#0c432a' }}>
                            {dayData.dayTotalSales.toFixed(2)}
                          </td>
                          <td colSpan={4}></td>
                        </tr>
                      ) : (
                        <tr
                          className='fw-bold bg-light'
                          style={{ borderBottom: '2px solid #0c432a' }}
                          ref={
                            dayIndex === groupedLists.length - 1
                              ? lastRowRef
                              : null
                          }
                        >
                          <td style={{ color: '#0c432a' }}>DAY TOTAL</td>
                          <td
                            className='text-end'
                            style={{ color: '#0c432a', whiteSpace: 'nowrap' }}
                          >
                            RM {dayData.dayTotalSales.toFixed(2)} (
                            {dayData.dayTotalQty})
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <tr>
                      <td
                        colSpan={isMobile ? '2' : '8'}
                        className='text-center py-3'
                      >
                        <div
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                        >
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        Loading more...
                      </td>
                    </tr>
                  )}

                  {!hasMore && !loadingMore && (
                    <tr>
                      <td
                        colSpan={isMobile ? '2' : '8'}
                        className='text-center py-3 text-muted'
                      >
                        - End of records -
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={isMobile ? '2' : '8'} className='text-center'>
                    Tiada rekod
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ResponseDetailsModal
        show={showResponseModal}
        onClose={closeResponseModal}
        recordId={selectedResponseId}
      />
    </Layout>
  )
}

export default MainSaleRecordList
